import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, BellRing, Building2, CalendarClock, ClipboardCheck, LifeBuoy, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, StatCard, StatusBadge, EmptyState } from "@/components/common";
import TaskDrawer from "@/components/TaskDrawer";
import ProgressPanel from "@/components/ProgressPanel";
import { Greeting, HealthCard, Attention } from "@/components/DashboardHub";
import { ClientHub } from "@/pages/client/ClientHub";
import { TaskOpen } from "@/components/compliances/ObligationDrawer";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/components/staff/Panel";
import { api, errMsg, fmtDate, TASK_ORDER, TASK_STATUS, pretty } from "@/lib/api";

const STAGE_PIPE = [["assessment", "Awaiting assessment"], ["responsibility", "Awaiting assignment"], ["in_progress", "In progress"], ["with_authority", "With authority"], ["verification", "Awaiting verification"]];

const StagePipeline = ({ d }) => (
  <div className="space-y-3" data-testid="stage-pipeline">
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
      {STAGE_PIPE.map(([k, l], i) => (
        <Link key={k} to={`/tasks?stage=${k}`} className="group rounded-xl border bg-card p-4 transition-colors hover:border-primary" data-testid={`pipeline-${k}`}>
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] text-black">{i + 1}</span>{l}</div>
          <div className="mt-2 font-heading text-3xl font-extrabold">{d.stage_counts?.[k] ?? 0}</div>
        </Link>
      ))}
    </div>
    <div className="flex flex-wrap gap-2 text-sm">
      <Link to="/documents" className="rounded-full border bg-card px-4 py-2 hover:border-primary" data-testid="pipeline-docs-pending">Documents pending review: <strong>{d.documents_pending_review ?? 0}</strong></Link>
      <Link to="/requests" className="rounded-full border bg-card px-4 py-2 hover:border-primary" data-testid="pipeline-requests">Open service requests: <strong>{d.open_requests}</strong></Link>
      {d.billing_alert?.overdue_invoices > 0 && <Link to="/billing?tab=invoices" className="rounded-full border border-accent bg-accent/30 px-4 py-2" data-testid="pipeline-billing-alert">Overdue invoices: <strong>{d.billing_alert.overdue_invoices}</strong> · TZS {d.billing_alert.overdue_amount.toLocaleString()}</Link>}
    </div>
  </div>
);


const ExpiringDocs = ({ docs }) => (
  <div className="rounded-lg border bg-card" data-testid="expiring-documents">
    <div className="flex items-center justify-between border-b px-5 py-3"><h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Expiring & expired documents</h3><Link to="/documents" className="text-xs text-primary hover:underline">Vault</Link></div>
    {!docs.length ? <p className="p-5 text-sm text-muted-foreground">No documents expiring soon.</p> : docs.map((d) => (
      <div key={d.id} className="flex items-center justify-between gap-3 border-b px-5 py-3 last:border-0" data-testid={`expiring-doc-${d.id}`}>
        <div className="min-w-0"><div className="truncate text-sm font-medium">{d.title}</div><div className="truncate text-xs text-muted-foreground">{pretty(d.doc_type) || pretty(d.category)} · expires {fmtDate(d.expiry_date)}</div></div>
        <StatusBadge kind="doc" status={d.status} />
      </div>
    ))}
  </div>
);

const BAR = { not_started: "bg-slate-300", in_progress: "bg-blue-500", awaiting_client: "bg-amber-500", submitted: "bg-sky-500", under_review: "bg-teal-500", returned: "bg-red-500", verified: "bg-emerald-600" };
const PIPE = [...TASK_ORDER.slice(0, 5), "returned", "verified"];

const StatusBar = ({ by }) => {
  const total = PIPE.reduce((a, s) => a + (by[s] || 0), 0) || 1;
  return (
    <div className="rounded-lg border bg-card p-5" data-testid="status-distribution">
      <h3 className="mb-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">Task pipeline</h3>
      <div className="flex h-3 overflow-hidden rounded-full bg-muted">{PIPE.map((s) => <div key={s} className={BAR[s]} style={{ width: `${((by[s] || 0) / total) * 100}%` }} />)}</div>
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">{PIPE.map((s) => <div key={s} className="flex items-center gap-2 text-xs"><span className={`h-2 w-2 rounded-full ${BAR[s]}`} />{TASK_STATUS[s].label}<strong className="ml-auto">{by[s] || 0}</strong></div>)}</div>
    </div>
  );
};

const Upcoming = ({ items, onOpen, showCompany }) => (
  <div className="rounded-lg border bg-card">
    <div className="border-b px-5 py-3"><h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Upcoming deadlines</h3></div>
    {!items.length ? <p className="p-5 text-sm text-muted-foreground">Nothing due.</p> : items.map((t) => (
      <button key={t.id} onClick={() => onOpen(t.id)} data-testid={`upcoming-${t.id}`} className="flex w-full items-center justify-between gap-3 border-b px-5 py-3 text-left last:border-0 hover:bg-muted/40">
        <div className="min-w-0"><div className="truncate text-sm font-medium">{t.title}</div><div className="truncate text-xs text-muted-foreground">{showCompany ? `${t.company_name} · ` : ""}{t.period_key}</div></div>
        <div className="flex shrink-0 flex-col items-end gap-1"><span className="text-xs font-semibold">{fmtDate(t.due_date)}</span><StatusBadge status={t.status} /></div>
      </button>
    ))}
  </div>
);

function StaffDashboard({ d, reload, onOpen }) {
  const { user } = useAuth();
  const run = async (path, label) => {
    try { const { data } = await api.post(path, path.includes("generate") ? {} : undefined); toast.success(`${label}: ${JSON.stringify(data)}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <Greeting subtitle={`${d.health?.need_you || 0} item(s) need your attention · workload across your companies`} />
      {d.health && <HealthCard h={d.health} />}
      <div className="mt-6"><Attention items={d.attention || []} onOpen={onOpen} showCompany /></div>
      <PageHeader title="Compliance workspace" subtitle="Workload across all client companies">
        {can(user, "tasks_generate") && <Button variant="outline" onClick={() => run("/tasks/generate", "Generated")} data-testid="generate-tasks-btn"><RefreshCw className="mr-1 h-4 w-4" /> Generate tasks</Button>}
        {can(user, "reminders_run") && <Button onClick={() => run("/reminders/run", "Reminders")} data-testid="run-reminders-btn"><BellRing className="mr-1 h-4 w-4" /> Send reminders</Button>}
      </PageHeader>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard label="Companies" value={d.companies} icon={Building2} testId="stat-companies" />
        <StatCard label="Overdue" value={d.overdue} icon={AlertTriangle} tone="danger" testId="stat-overdue" />
        <StatCard label="Due in 7 days" value={d.due_this_week} icon={CalendarClock} tone="warn" testId="stat-due-week" />
        <StatCard label="Review queue" value={d.review_queue} icon={ClipboardCheck} tone="ok" testId="stat-review-queue" />
        <StatCard label="Open requests" value={d.open_requests} icon={LifeBuoy} testId="stat-open-requests" />
      </div>
      <div className="mt-6"><StagePipeline d={d} /></div>
      {(d.pending_assistance > 0 || d.pending_approval > 0) && (
        <Link to="/queue" className="mt-6 flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm" data-testid="queue-alert">
          <span><strong>{d.pending_assistance}</strong> assistance request(s) awaiting decision · <strong>{d.pending_approval}</strong> generated task(s) awaiting approval</span><span className="font-semibold text-primary">Open V.A queue →</span>
        </Link>
      )}
      <div className="mt-6"><ProgressPanel /></div>
      {d.reassessment.length > 0 && (
        <div className="mt-6 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="reassessment-list">
          <p className="mb-2 text-sm font-semibold text-amber-900">Companies flagged for reassessment</p>
          <div className="flex flex-wrap gap-2">{d.reassessment.map((c) => <Link key={c.id} to={`/companies/${c.id}`} className="rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-medium hover:bg-amber-100">{c.name}</Link>)}</div>
        </div>
      )}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <StatusBar by={d.by_status} />
          <div className="overflow-x-auto rounded-lg border bg-card">
            <div className="border-b px-5 py-3"><h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Workload by company</h3></div>
            {!d.workload.length ? <p className="p-5 text-sm text-muted-foreground">No open tasks.</p> : (
              <table className="w-full text-sm" data-testid="workload-table">
                <thead className="text-left text-xs text-muted-foreground"><tr><th className="px-5 py-2">Company</th><th className="px-5 py-2">Open</th><th className="px-5 py-2">Overdue</th><th className="px-5 py-2">To review</th></tr></thead>
                <tbody>{d.workload.map((w) => (
                  <tr key={w.tenant_id} className="border-t"><td className="px-5 py-2.5"><Link to={`/companies/${w.tenant_id}`} className="font-medium hover:underline">{w.company}</Link></td><td className="px-5 py-2.5">{w.open}</td><td className={`px-5 py-2.5 ${w.overdue ? "font-bold text-red-600" : ""}`}>{w.overdue}</td><td className="px-5 py-2.5">{w.review}</td></tr>
                ))}</tbody>
              </table>
            )}
          </div>
        </div>
        <div className="space-y-6"><Upcoming items={d.upcoming} onOpen={onOpen} showCompany /><ExpiringDocs docs={d.expiring_documents} /></div>
      </div>
    </>
  );
}

export default function Dashboard() {
  const { isStaff } = useAuth();
  const ctx = useOutletContext();
  const [own, setOwn] = useState(null);
  const [open, setOpen] = useState(null);
  const loadOwn = () => api.get("/dashboard").then((r) => setOwn(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (isStaff) loadOwn(); }, [isStaff]); // eslint-disable-line
  const d = isStaff ? own : ctx?.dash;
  const load = isStaff ? loadOwn : ctx?.reload;
  if (!isStaff && ctx?.dashError && !d) return <EmptyState title="Could not load your dashboard" hint={ctx.dashError} testId="dashboard-error" action={<Button onClick={load} data-testid="dashboard-retry-btn">Try again</Button>} />;
  if (!d) return <EmptyState title="Loading dashboard…" testId="dashboard-loading" />;
  return (
    <div data-testid="dashboard">
      {isStaff ? <StaffDashboard d={d} reload={load} onOpen={setOpen} /> : <ClientHub d={d} onOpen={setOpen} />}
      {isStaff ? <TaskDrawer taskId={open} onClose={() => setOpen(null)} onChanged={load} /> : <TaskOpen taskId={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
